<template>
    <div id="manage-requests" class="relative h-full flex flex-col h-full p-4">
        <div>
            <div class="flex justify-start items-center h-10">
                <h1 class="m-0 truncate">{{ $t('manage_school_requests_header') }}</h1>
            </div>
        </div>
        <div id="requests-section" class="relative flex-1 overflow-hidden">
            <ManageTable
                @pagination-change="onPageChange"
                :list="requests"
                :current-page="currentPage"
                :page-count="pageCount"
                :items-per-page="itemsPerPage"
                :total-items="totalItems"
                :loading="loading"
                :disable-pagination="disableAll"
            >
                <template #header>
                    <h3 class="w-1/2 m-0 font-size-16">{{ $t('manage_school_table_header_name') }}</h3>
                    <h3 class="w-1/3 m-0 font-size-16 text-center">
                        {{ $t('manage_school_requests_table_header_request_date') }}
                    </h3>
                    <h3 class="w-1/6 m-0 font-size-16 text-right">
                        {{ $t('manage_school_requests_table_header_actions') }}
                    </h3>
                </template>
                <template v-slot="{ item, index }">
                    <div class="user-detail flex items-center w-full xl:w-1/2">
                        <div class="flex-1 overflow-hidden">
                            <div class="name flex items-center text-sm">
                                <span class="truncate">{{ item.name }}</span>
                            </div>
                            <div class="email text-xs truncate">
                                {{ item.email }}
                            </div>
                        </div>
                    </div>
                    <div class="w-full xl:w-1/3 text-sm xl:text-center">
                        <strong class="xl:hidden">
                            {{ $t('manage_school_requests_table_header_request_date') }}:
                        </strong>
                        <span>{{ item.created_at_readable }}</span>
                    </div>
                    <div class="settings flex justify-end w-full xl:w-1/6">
                        <div class="flex w-full xl:w-auto">
                            <XButton
                                v-tippy="{ placement: 'top' }"
                                @click="acceptRequest(item)"
                                :content="$t('manage_school_requests_table_tippy_accept')"
                                :class="`accept-request-button-${index} flex-1 ml-auto shadow-text-none`"
                                :disabled="item.acceptLoading || disableAll"
                                bg-color="#28a745"
                            >
                                <span v-if="item.acceptLoading" class="close-loader"></span>
                                <i v-else class="fas fa-check"></i>
                            </XButton>
                            <XButton
                                v-tippy="{ placement: 'top' }"
                                @click="rejectRequest(item)"
                                :content="$t('manage_school_requests_table_tippy_reject')"
                                :class="`reject-request-button-${index} flex-1 ml-auto shadow-text-none`"
                                :disabled="item.rejectLoading || disableAll"
                                bg-color="#dc3545"
                            >
                                <span v-if="item.rejectLoading" class="close-loader"></span>
                                <i v-else class="fas fa-times"></i>
                            </XButton>
                        </div>
                    </div>
                </template>
                <template #empty>
                    <div class="flex justify-center items-center w-full h-full">
                        <div class="flex flex-col justify-center items-center max-w-xl">
                            <i class="fas fa-child"></i>
                            <h4 v-html="$t('manage_school_requests_empty_state')" class="text-center m-0 mt-4"></h4>
                        </div>
                    </div>
                </template>
                <template #loading>
                    <InnerLoader color="#333" class="pin-t pin-l" />
                </template>
            </ManageTable>
        </div>
    </div>
</template>

<script>
import popup from '@/lib/popup.js'
import { load } from 'recaptcha-v3'
import ManageTable from '@/components/manage-school/ManageTable'
import XButton from '@/components/v2/XButton'
import InnerLoader from '@/components/InnerLoader'

export default {
    components: {
        ManageTable,
        XButton,
        InnerLoader
    },

    data() {
        return {
            currentPage: 1,
            pageCount: 0,
            itemsPerPage: 0,
            totalItems: 0,
            requests: null,
            loading: false,
            disableAll: false
        }
    },

    created() {
        this.fetchSchoolRequests()
    },

    methods: {
        async fetchSchoolRequests() {
            this.loading = true
            this.disableAll = true

            this.users = null

            const urlParams = new URL(window.location.href)

            this.currentPage = urlParams.searchParams.get('page') || 1

            try {
                const res = await this.$store.dispatch('getSchoolRequests', {
                    params: {
                        page: this.currentPage
                    }
                })

                this.requests = res.data.map(request => {
                    return {
                        ...request,
                        acceptLoading: false,
                        rejectLoading: false
                    }
                })

                this.currentPage = res.current_page
                this.pageCount = res.page_count
                this.itemsPerPage = res.perpage
                this.totalItems = res.total
            } catch (err) {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.loading = false
                this.disableAll = false
            }
        },

        onPageChange(value) {
            this.currentPage = value

            const urlParams = new URL(window.location.href)

            urlParams.searchParams.set('page', this.currentPage)

            window.history.replaceState(null, null, urlParams.toString())

            this.fetchSchoolRequests()
        },

        acceptRequest(user) {
            if (this.disableAll || user.acceptLoading) return

            popup.confirm(this.$t('manage_school_requests_confirm_accept_user'), async () => {
                this.disableAll = true
                user.acceptLoading = true

                try {
                    if (user.user_id) {
                        await this.$store.dispatch('addToSchool', {
                            access_request_id: user._id.$oid,
                            user_id: user.user_id.$oid
                        })

                        this.toastSuccess(this.$t('manage_school_requests_popup_accept_user_complete'))
                    } else {
                        const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                            useRecaptchaNet: true,
                            autoHideBadge: true
                        })

                        const token = await recaptcha.execute('validate_captcha', {
                            action: 'validate_captcha'
                        })

                        await this.$store.dispatch('inviteToSchool', {
                            access_request_id: user._id.$oid,
                            emails: [user.email],
                            captcha_response: token
                        })

                        this.toastSuccess(this.$t('manage_school_requests_popup_accept_invite_user_complete'))
                    }

                    this.fetchSchoolRequests()
                } catch (err) {
                    this.toastError(this.$t('manage_school_popup_error'))
                    user.acceptLoading = false
                } finally {
                    this.disableAll = false
                }
            })
        },

        rejectRequest(user) {
            if (this.disableAll || user.rejectLoading) return

            popup.confirm(this.$t('manage_school_requests_confirm_reject_user'), async () => {
                this.disableAll = true
                user.rejectLoading = true

                try {
                    const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                        useRecaptchaNet: true,
                        autoHideBadge: true
                    })

                    const token = await recaptcha.execute('validate_captcha', {
                        action: 'validate_captcha'
                    })

                    await this.$store.dispatch('rejectAccessSchool', {
                        access_request_id: user._id.$oid,
                        user_id: user.user_id,
                        captcha_response: token
                    })

                    this.toastSuccess(this.$t('manage_school_requests_popup_reject_user_complete'))

                    this.fetchSchoolRequests()
                } catch (err) {
                    this.toastError(this.$t('manage_school_popup_error'))
                    user.rejectLoading = false
                } finally {
                    this.disableAll = false
                }
            })
        }
    }
}
</script>

<style lang="scss">
#manage-requests {
    row-gap: 1rem;

    #requests-section {
        row-gap: 1rem;

        .manage-table-item {
            row-gap: 1rem;

            .user-detail {
                column-gap: 1rem;

                .avatar {
                    width: 2rem;
                    height: auto !important;
                    aspect-ratio: 1 / 1;
                    background-color: gray;
                }

                .name {
                    column-gap: 0.3rem;
                }
            }

            .settings {
                > div {
                    column-gap: 0.5rem;

                    button {
                        width: 100%;
                        height: 2rem;

                        @media screen and (min-width: 1200px) {
                            width: 2rem;
                            height: auto;
                            padding: 0;
                            aspect-ratio: 1 / 1;
                        }
                    }
                }
            }
        }
    }
}
</style>